import { useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Heart, X, BookOpen } from "lucide-react";

const IntentionModal = ({ isOpen, onClose, onConfirm, targetName }) => {
  const [checkedItems, setCheckedItems] = useState({
    deen: false,
    character: false,
    intentions: false,
    expectations: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allChecked = Object.values(checkedItems).every(v => v);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    await onConfirm();
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-serif text-xl text-stone-900">A Moment of Reflection</h2>
                <p className="text-stone-500 text-sm">Before expressing interest</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-stone-600 mb-6 leading-relaxed">
            Marriage is a significant step in your deen. Before reaching out to <span className="font-medium text-stone-900">{targetName}</span>, take a moment to reflect on your intentions.
          </p>

          <div className="bg-emerald-50 rounded-2xl p-4 mb-6">
            <p className="text-emerald-800 text-sm italic">
              "A woman is married for four things: her wealth, her lineage, her beauty, and her religion. So marry the one with religion." — Prophet Muhammad ﷺ
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <p className="font-medium text-stone-900">Please confirm:</p>
            
            <label className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <Checkbox 
                id="deen"
                checked={checkedItems.deen}
                onCheckedChange={(checked) => setCheckedItems(prev => ({ ...prev, deen: checked }))}
                className="mt-0.5"
              />
              <div>
                <span className="text-stone-900 font-medium block">I am considering their deen first</span>
                <span className="text-stone-500 text-sm">Religious commitment and practice are my priority</span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <Checkbox 
                id="character"
                checked={checkedItems.character}
                onCheckedChange={(checked) => setCheckedItems(prev => ({ ...prev, character: checked }))}
                className="mt-0.5"
              />
              <div>
                <span className="text-stone-900 font-medium block">I value their character (akhlaq)</span>
                <span className="text-stone-500 text-sm">Kindness, honesty, and good manners matter to me</span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <Checkbox 
                id="intentions"
                checked={checkedItems.intentions}
                onCheckedChange={(checked) => setCheckedItems(prev => ({ ...prev, intentions: checked }))}
                className="mt-0.5"
              />
              <div>
                <span className="text-stone-900 font-medium block">My intentions are sincere</span>
                <span className="text-stone-500 text-sm">I am genuinely seeking marriage, not casual conversation</span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <Checkbox 
                id="expectations"
                checked={checkedItems.expectations}
                onCheckedChange={(checked) => setCheckedItems(prev => ({ ...prev, expectations: checked }))}
                className="mt-0.5"
              />
              <div>
                <span className="text-stone-900 font-medium block">I will communicate my expectations clearly</span>
                <span className="text-stone-500 text-sm">I understand that honest communication prevents misunderstandings</span>
              </div>
            </label>
          </div>

          <p className="text-stone-500 text-sm text-center mb-6">
            May Allah bless your search and guide you to what is best for your deen and dunya.
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-stone-100 bg-stone-50 rounded-b-3xl">
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-stone-300 text-stone-600 rounded-full h-12"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!allChecked || isSubmitting}
              className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white rounded-full h-12 disabled:opacity-50"
            >
              {isSubmitting ? (
                "Sending..."
              ) : (
                <>
                  <Heart className="w-4 h-4 mr-2" />
                  Express Interest
                </>
              )}
            </Button>
          </div>
          {!allChecked && (
            <p className="text-center text-stone-400 text-sm mt-3">
              Please confirm all points to continue
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntentionModal;
